import { QRCodeSVG } from 'qrcode.react';
import styled from 'styled-components';
import { tokens } from '../../../public/tokens';
import { Banner } from '../../library/Banner';
import { MOBILE_MEDIA_QUERY } from '../../library/breakpoints';
import { iconBadge, listResetStyle } from '../../library/primitives';
import { bodyText, labelText } from '../../library/text';

const { colors, typography, globals } = tokens;

export const OptionsGrid = styled.div(({ $isMobile }) => ({
    display: 'flex',
    gap: globals['spacing-lg'],
    alignItems: 'stretch',
    flexDirection: $isMobile ? 'column' : 'row',

    [MOBILE_MEDIA_QUERY]: {
        gap: globals['spacing-md'],
    },
}));

/**
 * Column inside the options panel: face guide or QR code stacked above the
 * upload/capture buttons.
 */
export const OptionsColumn = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: globals['spacing-md'],
});

export const OptionsHeading = styled.strong({
    ...labelText,
});

export const OptionsHint = styled.p({
    ...bodyText,
    textAlign: 'center',
});

export const PreviewPanel = styled.div(({ $isMobile }) => ({
    display: 'flex',
    flexDirection: $isMobile ? 'column' : 'row',
    gap: globals['spacing-lg'],
    alignItems: 'stretch',

    borderColor: colors.ui.border,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderRadius: '20px',
    padding: '12px',
    background: colors.surfaces['container-background'],
}));

/**
 * Back/Submit row under the preview panel.
 */
export const ActionRow = styled.div({
    display: 'flex',
    gap: globals['spacing-sm'],
    marginTop: globals['spacing-lg'],
});

export const RequirementsHeader = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
});

export const RequirementsTitle = styled.div({
    ...labelText,
    color: colors.neutral.grey['800'],
    paddingTop: '2px',
});

export const RequirementsIcon = styled.span({
    ...iconBadge,
    width: '24px',
    height: '24px',
    color: colors.states.warning.default,
});

export const RequirementsList = styled.ul({
    ...listResetStyle,
    gap: globals['spacing-sm'],
    fontSize: typography.fontSize['lg-14px'],
    color: colors.text.secondary,

    li: {
        paddingLeft: globals['spacing-md'],
        position: 'relative',

        '::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: globals['spacing-sm'],
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            background: colors.text.secondary,
        },
    },
});

/**
 * The QR code itself, rendered client-side as an SVG. QRCodeSVG spreads any props it
 * doesn't recognise onto its <svg>, so the generated className lands where it should.
 */
export const QrCode = styled(QRCodeSVG)(({ $isDimmed }) => ({
    maxWidth: '100%',
    height: 'auto',
    opacity: $isDimmed ? 0.5 : 1,
}));

/**
 * Anchors the "photo incoming" spinner over the QR code it dims.
 */
export const QrFrame = styled.div({
    position: 'relative',
    display: 'inline-flex',
});

export const QrOverlay = styled.div({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 0,
    background: `color-mix(in srgb, ${colors.neutral.white} 80%, transparent)`,
    color: colors.text.secondary,
    fontSize: typography.fontSize['xl-18px'],
});

export const RefreshQrLink = styled.button({
    background: 'none',
    border: 'none',
    padding: 0,
    color: colors.text.link,
    fontSize: typography.fontSize['md-12px'],
    cursor: 'pointer',

    ':disabled': {
        color: colors.text.disabled,
        cursor: 'default',
    },
});

export const ImagePanel = styled.div({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: globals['border-radius'],
    background: colors.neutral.grey['800'],
    height: '320px',
    flex: 5,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

export const FaceOval = styled.div({
    position: 'absolute',
    top: '42%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: '60%',
    aspectRatio: '251 / 328',
    borderRadius: '50%',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: `color-mix(in srgb, ${colors.neutral.white} 70%, transparent)`,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
    pointerEvents: 'none',
    opacity: 0.4,
});

export const ScanningBadge = styled.div({
    position: 'absolute',
    top: '12px',
    right: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '999px',
    background: `color-mix(in srgb, ${colors.neutral.white} 85%, transparent)`,
    color: colors.text.primary,
    fontSize: typography.fontSize['md-12px'],
    fontWeight: typography.fontWeights.semibold,
});

export const CaptureControls = styled.div({
    position: 'absolute',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
});

export const ImageHint = styled.div({
    color: colors.neutral.white,
    textAlign: 'center',
    fontSize: typography.fontSize['sm-10px'],
    padding: `0 ${globals['spacing-md']}`,
});

export const CaptureButton = styled.button({
    width: '31px',
    height: '31px',
    borderRadius: '50%',
    background: colors.brand.red['500'],
    border: `3px solid ${colors.neutral.white}`,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
});

export const SuccessContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: globals['spacing-lg'],
    padding: `${globals['spacing-2xl']} ${globals['spacing-2xl']} 36px`,
    background: colors.neutral.white,
    borderRadius: globals['border-radius'],
    width: '100%',
    flex: 1,
});

export const SuccessTitle = styled.div({
    fontFamily: globals['font-family'],
    fontStyle: 'normal',
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize['4xl-32px'],
    lineHeight: typography.lineHeights['110'],
    color: colors.text.primary,
    maxWidth: '500px',
});

/**
 * Round avatar for the student's enrolled profile photo. `$size` carries its own
 * unit (e.g. '42px').
 */
export const ProfilePhotoImage = styled.img(({ $size }) => ({
    width: $size,
    height: $size,
    borderRadius: '50%',
    objectFit: 'cover',
}));

/**
 * Camera-icon circle standing in until a photo is enrolled.
 */
export const ProfilePhotoPlaceholder = styled.div(({ $size }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: $size,
    height: $size,
    minWidth: $size,
    borderRadius: '50%',
    backgroundColor: '#C5D8F3', // hardcoded per design, no matching token in tokens.js

    i: {
        color: '#0949A2', // hardcoded per design, no matching token in tokens.js
    },
}));

export const StudentIdentityLayout = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
});

export const StudentIdentity = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: globals['spacing-sm'],
    marginBottom: globals['spacing-md'],
});

/**
 * Stacks the student's name above their status pill, beside the avatar.
 */
export const NameBlock = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0,
});

export const StudentName = styled.div({
    fontWeight: typography.fontWeights.bold,
    fontSize: typography.fontSize['lg-14px'],
    color: colors.text.primary,
});

/**
 * Grouped container for student identity details (student number, email,
 * and any future fields), each separated by a divider.
 */
export const InfoList = styled.div({
    display: 'flex',
    flexDirection: 'column',
    padding: '12px',
    gap: '12px',
    alignSelf: 'stretch',
    backgroundColor: colors.surfaces['page-background'],
    borderRadius: globals['border-radius'],
    marginBottom: globals['spacing-lg'],
});

export const InfoItem = styled.div({
    paddingTop: '12px',
    borderTopColor: colors.neutral.grey['300'],
    borderTopStyle: 'solid',
    borderTopWidth: '0.8px',
    color: colors.neutral.grey['700'],
    fontFamily: globals['font-family'],
    fontSize: typography.fontSize['lg-14px'],
    fontWeight: typography.fontWeights.regular,
    lineHeight: '14px',

    ':first-child': {
        paddingTop: 0,
        borderTopWidth: 0,
    },
});

export const InfoItemContent = styled.span({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
});

/**
 * Spaces the capture card off the requirements card below it. A wrapper rather than
 * an extension of SectionContainerWrapper, which is an @emotion component - mixing
 * the two style libraries on one element leaves the winner up to injection order.
 */
export const CaptureSectionSpacing = styled.div({
    marginBottom: globals['spacing-lg'],

    [MOBILE_MEDIA_QUERY]: {
        marginBottom: globals['spacing-md'],
    },
});

/**
 * Face outline the student lines their headshot up against.
 */
export const FaceGuideImage = styled.img({
    display: 'block',
    width: '100%',
    maxWidth: '220px',
    margin: `0 auto ${globals['spacing-lg']}`,
});

/**
 * Tappable "photo sent" confirmation on the success screen. Centred and bold,
 * since it doubles as the close affordance.
 */
export const CloseBanner = styled(Banner)({
    marginTop: globals['spacing-xl'],
    marginBottom: 0,
    fontWeight: typography.fontWeights.bold,
    textAlign: 'center',
});
